import { useMutation } from '@tanstack/react-query';
import { bulkSendMessage } from 'src/models/client/client';
import { DeliveryMethod, MediaUpload } from 'src/models/conversation/_types';
import { startNewConversation } from 'src/models/conversation/conversation';
import { getUserDisplayName } from 'src/models/user/utils';
import { MessageDialog as SharedMessageDialog } from 'src/shared/Dialogs/MessageDialog';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';
import { Identifier } from 'types/utilityTypes';

import { BaseDialogProps } from './_types';

export const MessageDialog = ({
  clients,
  users,
  selectAll,
  onClose,
  onSuccess,
}: BaseDialogProps) => {
  const { params, filterParams } = useListSearchParams({ paramPrefix: 'client_' });

  let defaultUsers: { id: Identifier; nickname: string }[] = [];

  if (users && users.length > 0) {
    defaultUsers = users.map((user) => ({
      id: user.id,
      nickname: getUserDisplayName(user) || user.email || '',
    }));
  } else if (clients && clients.length > 0) {
    defaultUsers = clients.map((client) => ({
      id: client.userId,
      nickname: client.clientName || client.clientEmail,
    }));
  }

  const formattedFilters = selectAll
    ? {
        ...filterParams,
        customStatus: filterParams?.customStatus?.value
          ? [
              filterParams.customStatus.value,
              filterParams.customStatus.number,
              filterParams.customStatus.start,
              filterParams.customStatus.end,
            ].join(',')
          : undefined,
      }
    : undefined;

  const { mutate: sendMessage, isPending } = useMutation({
    mutationFn: async ({
      message,
      files,
      delivery,
      subject,
    }: {
      message: string;
      files: MediaUpload[];
      delivery?: DeliveryMethod[];
      subject?: string;
    }) => {
      let messageUserIds: Identifier[] = [];

      if (users && users.length > 0) {
        messageUserIds = users.map((user) => user.id);
      } else if (clients && clients.length > 0) {
        messageUserIds = clients.map((client) => client.userId);
      }

      if (selectAll) {
        return bulkSendMessage({
          userIds: messageUserIds,
          message,
          delivery: delivery || ['in-app', 'email'],
          selectAll: true,
          search: params.search,
          filters: formattedFilters,
        });
      }

      return startNewConversation({
        userIds: messageUserIds,
        message,
        files,
        delivery,
        subject,
      });
    },

    onSuccess: () => {
      const message =
        selectAll || (clients && clients.length > 10)
          ? 'Messages are being sent in the background. It may take a minute.'
          : 'Messages have been sent successfully!';
      toast.success(message);
      onSuccess?.();
      onClose();
    },

    onError: (error: any) => {
      toast.error(api.formatError(error, 'Unable to send message(s).'));
    },
  });

  const handleSend = (
    message: string,
    files: MediaUpload[],
    delivery?: DeliveryMethod[]
  ) => {
    sendMessage({ message, files, delivery });
  };

  const title = selectAll
    ? 'Send Message to all accounts on all pages'
    : `Send Message to ${defaultUsers.length} account${
        defaultUsers.length === 1 ? '' : 's'
      }`;

  return (
    <SharedMessageDialog
      open={true}
      onClose={onClose}
      title={title}
      defaultMessage=""
      defaultUsers={defaultUsers}
      onSend={handleSend}
      disableSendButton={isPending}
    />
  );
};
